import { AvatarInfoService } from '@wdx/clmi/api-services/services';
import { Avatar, AvatarDetailPopup } from '@wdx/shared/components/wdx-avatar';
import {
    PartyStub,
    STATUS_THEMECOLOR_MAP,
    SystemEntity,
    WdxThemeColor,
} from '@wdx/shared/utils';
import { Observable, map } from 'rxjs';
import { ICellRendererParams } from 'ag-grid-community';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import {
    AG_GRID_VALUE_GETTER_BY_FIELD,
    AvatarCellRendererComponent,
    AvatarCellRendererValue,
    avatarCellRendererComparator,
} from '@wdx/shared/components/ag-grid';

/**
 *
 * This file provides functions to bind the CLMi specific Avatar service/pipe to the shared AG Grid components
 * in @wdx/shared/components/ag-grid.
 *
 * NOTE: Only copy this approach if there are application specific things you need to pass into AG Grid, where
 * this is not the case provide these functions in in @wdx/shared/components/ag-grid.
 *
 */

export const AVATAR_CELL_RENDERER_PARAMS = (
    avatarInfoService: AvatarInfoService,
    entityType?: SystemEntity,
) => ({
    popupContent$(avatar: Avatar): Observable<AvatarDetailPopup> {
        return avatarInfoService
            .getForEntityId(entityType || SystemEntity.Party, avatar.partyId)
            .pipe(
                map((info) => ({
                    name: info.name,
                    info: info.jobTitle,
                    emails: info.emailAddresses?.map((email) => email.name),
                    phoneNumbers: info.phoneAddresses?.map(
                        (phone) => phone.name,
                    ),
                    badge: {
                        themeColor: STATUS_THEMECOLOR_MAP[info.status],
                        status: info.status,
                    },
                })),
            );
    },
});

export const AVATAR_CELL_RENDERER_SELECTOR =
    (
        partyToAvatarPipe: PartyToAvatarPipe,
        options?: {
            showLabel: boolean;
            url?: string[];
            newTab?: boolean;
            badge?: {
                label: string;
                color?: WdxThemeColor;
            };
            linkClickCallback?: () => void;
        },
        partyStub?: PartyStub,
    ) =>
    (params: ICellRendererParams) => {
        const value = partyStub
            ? partyStub
            : AG_GRID_VALUE_GETTER_BY_FIELD(params);
        const valueArray = Array.isArray(value) ? value : value ? [value] : [];
        const avatars = valueArray.map((avatar) =>
            partyToAvatarPipe.transform(avatar),
        );
        const label =
            options.showLabel && avatars.length === 1 ? value?.name : undefined;
        return {
            component: AvatarCellRendererComponent,
            params: {
                value: {
                    avatars,
                    label,
                    url: options?.url,
                    newTab: options?.newTab,
                    badge: options?.badge,
                    linkClickCallback: options?.linkClickCallback,
                } as AvatarCellRendererValue,
            },
        };
    };

export const AVATAR_CELL_RENDERER_CONFIG = (
    avatarInfoService: AvatarInfoService,
    partyToAvatarPipe: PartyToAvatarPipe,
    showLabel = false,
    popupEntityType?: SystemEntity,
) => ({
    cellRendererParams: AVATAR_CELL_RENDERER_PARAMS(
        avatarInfoService,
        popupEntityType,
    ),
    comparator: avatarCellRendererComparator,
    cellRendererSelector: AVATAR_CELL_RENDERER_SELECTOR(partyToAvatarPipe, {
        showLabel,
    }),
});
